import { rem } from "@certn/cascadia";
import { ActionListItem, Text } from "@certn/cascadia-react";
import { GhostIconButton } from "@certn/cascadia-react/components/button/GhostIconButton";
import { StepType } from "@certn/cascadia-react/components/stepper/ActivityStepper";
import { ProgressStepper } from "@certn/cascadia-react/components/stepper/ProgressStepper";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { formatDate } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  CheckStatus,
  CheckStatusType,
} from "../../../components/CheckStatus/CheckStatus";

type HistoryCardProps = {
  superteamName: string;
  activityLog: readonly components["schemas"]["ApplicationsListActivityLog"][];
  archived?: boolean;
  applicationExpiryDate?: string | null;
};

type Application = components["schemas"]["ApplicationsList"];

type ActivityLogs = Application["activity_log"];

const config: Partial<
  Record<
    components["schemas"]["ApplicationsListActivityLog"]["status"],
    {
      type: CheckStatusType;
      iconText: React.ReactElement;
      statusText: React.ReactElement;
    }
  >
> = {
  COMPLETE: {
    type: "complete",
    iconText: <Trans>Complete</Trans>,
    statusText: <Trans>Completed</Trans>,
  },
  SUBMITTED: {
    type: "processing",
    iconText: <Trans>Processing</Trans>,
    statusText: <Trans>Submitted</Trans>,
  },
  DISPUTE: {
    type: "dispute",
    iconText: <Trans>In dispute</Trans>,
    statusText: <Trans>Submitted</Trans>,
  },
  MOVED_ARCHIVED: {
    type: "archived",
    iconText: <Trans>Archived</Trans>,
    statusText: <Trans>Expired</Trans>,
  },
  DECLINE_REQUEST: {
    type: "archived",
    iconText: <Trans>Archived</Trans>,
    statusText: <Trans>Cancelled</Trans>,
  },
  CANCELLED_APPLICATION: {
    type: "archived",
    iconText: <Trans>Archived</Trans>,
    statusText: <Trans>Cancelled</Trans>,
  },
};

const statusToDisplayDateMapping: {
  [key: string]: (
    activityLog: ActivityLogs,
    expiryDate?: string | null
  ) => Date | string;
} = {
  MOVED_ARCHIVED: (activityLog, expiryDate) =>
    expiryDate ?? activityLog[activityLog.length - 1].created,
  COMPLETE: (activityLog) => activityLog[activityLog.length - 1].created,
  SUBMITTED: (activityLog) =>
    activityLog.find((log) => log.status === "SUBMITTED")?.created ?? "",
  DISPUTE: (activityLog) =>
    activityLog.find((log) => log.status === "SUBMITTED")?.created ?? "",
  DECLINE_REQUEST: (activityLog) =>
    activityLog.find((log) => log.status === "DECLINE_REQUEST")?.created ?? "",
  CANCELLED_APPLICATION: (activityLog) =>
    activityLog.find((log) => log.status === "CANCELLED_APPLICATION")
      ?.created ?? "",
};

export function HistoryCard({
  superteamName,
  activityLog,
  archived,
  applicationExpiryDate,
}: HistoryCardProps) {
  const [showProgress, setShowProgress] = useState(false);
  const { t } = useTranslation();

  const lastStatusInLog = activityLog[activityLog.length - 1].status;
  const applicationCancelled = activityLog.some(
    (entry) => entry.status === "CANCELLED_APPLICATION"
  );

  // if application is cancelled at anytime we should use CANCELLED_APPLICATION
  // if application not cancelled and archived=true we can use MOVED_ARCHIVED
  // otherwise base status display on last entry in activity log
  const configStatus = applicationCancelled
    ? "CANCELLED_APPLICATION"
    : archived
      ? "MOVED_ARCHIVED"
      : lastStatusInLog;

  const completedDate = statusToDisplayDateMapping[configStatus](
    activityLog,
    applicationExpiryDate
  );

  const statusConfig = config[configStatus];

  if (!statusConfig) {
    return null;
  }

  const submittedLogIndex = activityLog.findIndex(
    (log) => log.status === "SUBMITTED"
  );
  const slicedActivityLog = activityLog.slice(submittedLogIndex);
  const steps: StepType[] = [];

  slicedActivityLog.forEach((log, index) => {
    const lastActivity = index === slicedActivityLog.length - 1;
    if (log.status === "SUBMITTED") {
      steps.push({
        title: t("Submitted"),
        description:
          t("You submitted the application to Certn on ") +
          formatDate(log.created, "PPP"),
        status: "COMPLETE",
      });
      steps.push({
        title: t("Processing"),
        description: lastActivity
          ? t("Certn is currently processing the report")
          : t("Certn sent the report to ") +
            superteamName +
            " " +
            formatDate(completedDate, "PPP"),
        status: lastActivity ? "INCOMPLETE" : "COMPLETE",
      });
    } else if (log.status === "UPGRADED") {
      steps.push({
        title: t("Follow up check requested"),
        description: t("Additional checks issued"),
        status: "COMPLETE",
      });
    } else if (log.status === "DISPUTE") {
      steps.push({
        title: t("In dispute"),
        description: lastActivity
          ? t("Certn is currently processing the report dispute")
          : t("Completed") + " " + formatDate(log.created, "PPP"),
        status: lastActivity ? "INCOMPLETE" : "COMPLETE",
      });
    } else if (log.status === "COMPLETE") {
      steps.push({
        title: t("Complete"),
        description: t("Completed") + " " + formatDate(log.created, "PPP"),
        status: "COMPLETE",
      });
    }
  });

  if (steps[steps.length - 1]?.title !== t("Complete")) {
    steps.push({
      title: t("Complete"),
      description: "",
      status: "INCOMPLETE",
    });
  }

  return (
    <ActionListItem
      title={superteamName}
      tag={
        // title is above children but below tag/trailling, this component requires a margin
        <div className={css({ marginBottom: rem("space-3") })}>
          <CheckStatus hasGrayBackground type={statusConfig.type}>
            {statusConfig.iconText}
          </CheckStatus>
        </div>
      }
      trailing={
        !archived && (
          <GhostIconButton
            testID={
              showProgress
                ? "history-card-show-less-button"
                : "history-card-show-more-button"
            }
            color="icon-base"
            icon={showProgress ? "expand_less" : "expand_more"}
            onPress={() => setShowProgress((value) => !value)}
            size="small"
            aria-label={showProgress ? t("Show Less") : t("Show More")}
            aria-expanded={showProgress}
          />
        )
      }
    >
      <Text>
        <Trans>Background check application</Trans>
      </Text>
      <Text>
        {statusConfig.statusText} {formatDate(completedDate, "PP")}
      </Text>
      {showProgress && (
        <div
          style={{ marginTop: rem("space-4") }}
          data-testid="progress-stepper"
        >
          <ProgressStepper steps={steps} />
        </div>
      )}
    </ActionListItem>
  );
}
